import {isNumber} from 'lodash';
import React, {FC, PropsWithChildren} from 'react';
import styled, {css} from 'styled-components';

import {fontSizes, fontWeights} from '../../styles/fontStyles';
import {shadows} from '../../styles/shadowStyles';
import {RepositionPopover, RepositionPopoverProps} from '../popovers/repositionPopover';
import {RepositionPlacement} from '../reposition/repositionHelpers';
import {getTooltipAnimationCss} from './tooltipAnimations';

interface TooltipProps extends RepositionPopoverProps {
  isMultiline?: boolean;
  customMargin?: number | string;
  maxWidth?: number;
  minWidth?: number;
  placement?: RepositionPlacement;
  persistent?: boolean;
  color?: string;
  borderRadius?: number;
  isBoxShadowDisabled?: boolean;
  zIndex?: number;
  /** This removes the styled wrapper - it should only be used by TooltipCoordinator. It allows the use of new Tooltip presentaional components ./tooltipContents */
  shouldRemoveWrapper?: boolean;
}

/*
 * Style.
 */

interface TooltipStyleProps {
  $isMultiline?: boolean;
  $customMargin?: number | string;
  $maxWidth?: number;
  $minWidth?: number;
  $color?: string;
  $borderRadius?: number;
  $isBoxShadowDisabled?: boolean;
  $zIndex?: number;
  $isDarkTheme?: boolean;
  $placement: RepositionPlacement;
}
const StyledTooltipDiv = styled.div<TooltipStyleProps>`
  padding: 6px 8px;
  max-width: ${(p) => `${p.$maxWidth || 240}px`};
  min-width: ${(p) => `${p.$minWidth || 0}px`};
  ${(p) =>
    p.$zIndex &&
    css`
      z-index: ${p.$zIndex};
    `};

  border-radius: ${(p) => `${p.$borderRadius || 8}px`};
  background-color: ${(p) => p.$color || p.theme.tooltip.background};

  ${(p) => !p.$isBoxShadowDisabled && shadows.tooltip};
  margin: ${(p) => getMarginStyles(p)};

  color: ${(p) => p.theme.tooltip.text};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.normal};
  line-height: 16px;

  ${(p) => getTooltipAnimationCss(p.$placement)};
`;

function getMarginStyles(props: Pick<TooltipStyleProps, '$customMargin' | '$isMultiline'>) {
  if (props.$customMargin) {
    return isNumber(props.$customMargin) ? `${props.$customMargin}px` : props.$customMargin;
  }

  if (props.$isMultiline) {
    return '7px';
  }

  return 'auto';
}

const StyledMarginDiv = styled.div<Pick<TooltipStyleProps, '$customMargin' | '$placement'>>`
  margin: ${getMarginStyles};
  ${(p) => getTooltipAnimationCss(p.$placement)};
`;

/*
 * Component.
 */

export const Tooltip: FC<PropsWithChildren<TooltipProps>> = (props) => {
  const placement = props.placement || 'bottom';

  const isInteractive = Boolean(props.persistent);

  return (
    <RepositionPopover {...props} isInteractive={isInteractive} placement={placement} shouldSkipApplyMaxSize>
      {props.shouldRemoveWrapper ? (
        <StyledMarginDiv $customMargin={props.customMargin} $placement={placement}>
          {props.children}
        </StyledMarginDiv>
      ) : (
        <StyledTooltipDiv
          $isMultiline={props.isMultiline}
          $customMargin={props.customMargin}
          $maxWidth={props.maxWidth}
          $minWidth={props.minWidth}
          $color={props.color}
          $borderRadius={props.borderRadius}
          $isBoxShadowDisabled={props.isBoxShadowDisabled}
          $zIndex={props.zIndex}
          $placement={placement}
        >
          {props.children}
        </StyledTooltipDiv>
      )}
    </RepositionPopover>
  );
};
